import {
  Checkbox,
  Dropdown,
  FormItem,
  IconButton,
  Modal,
  TextField,
  Tooltip,
} from '@getsynapse/design-system';
import { ModalProps } from '@getsynapse/design-system/dist/Organisms/Modal/Modal';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import {
  duplicateBudget,
  selectBudgetSettings,
} from 'state/BudgetList/budgetListSlice';
import {
  CREATE_BUDGET_FIELDS,
  LONG_INPUTS_LENGTH,
  MONTHS,
} from 'utils/constants';

interface BudgetInfo {
  id: string;
  name: string;
  year_start: Date;
}

export type DuplicateBudgetModalProps = ModalProps & {
  onDuplicateBudget: (data: any) => void;
  onCancel: () => void;
  fromBudget: BudgetInfo;
};

export type DuplicateBudgetFormFields = {
  name: string;
  year_start: string;
  carryActual: boolean;
};

export const DuplicateBudgetModal = ({
  onDuplicateBudget,
  onCancel,
  fromBudget,
  ...props
}: DuplicateBudgetModalProps) => {
  const dispatch = useDispatch();
  const budgetSettings = useSelector(selectBudgetSettings);

  const toSentenceCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const fiscalYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    if (!budgetSettings) {
      return [];
    }

    const thisFiscalYearOptions = [];

    const fiscalMonthStart: number = budgetSettings?.fiscalYearStartMonth ?? 0;
    const monthName = toSentenceCase(MONTHS[fiscalMonthStart]);
    const monthValue = (fiscalMonthStart + 1).toString().padStart(2, '0');

    for (let i = -1; i <= 1; i++) {
      const year = currentYear + i;

      thisFiscalYearOptions.push({
        label: `${monthName} ${year}`,
        value: `${year}-${monthValue}-01`,
      });
    }

    return thisFiscalYearOptions;
  }, [budgetSettings]);

  useEffect(() => {
    if (!budgetSettings) {
      return;
    }

    const fiscalMonthStart: number = budgetSettings?.fiscalYearStartMonth ?? 0;
    const monthValue = (fiscalMonthStart + 1).toString().padStart(2, '0');

    // fiscalPeriodStart is typically an ISO date-only string (UTC). Use UTC parts
    // so the derived value matches option values; local getFullYear() can be wrong
    // (e.g. Jan 1 UTC → prior calendar year in US timezones).
    const y = fromBudget.year_start.getUTCFullYear();
    let fy_start = `${y}-${monthValue}-01`;
    if (!fiscalYearOptions.find((i) => i.value === fy_start)) {
      fy_start = '';
    }
    setFieldsValues({
      name: `${fromBudget.name} - Copy`,
      year_start: fy_start,
      carryActual: false,
    });
  }, [budgetSettings, fiscalYearOptions, fromBudget]);

  const [fieldsValues, setFieldsValues] = useState<DuplicateBudgetFormFields>({
    name: '',
    year_start: '',
    carryActual: false,
  });

  const canSubmit = useMemo(() => {
    return fieldsValues.name.trim().length > 0 && fieldsValues.year_start;
  }, [fieldsValues]);

  const selectedFiscalYearOption = useMemo(() => {
    return fiscalYearOptions.find((i) => i.value === fieldsValues.year_start);
  }, [fiscalYearOptions, fieldsValues.year_start]);

  const updateValue = (newValue: any) => {
    setFieldsValues((prevData) => ({ ...prevData, ...newValue }));
  };

  const handlePickYearStart = (yearId: any) => {
    updateValue({ year_start: yearId.value });
  };

  const handleDuplicateBudget = useCallback(async () => {
    const data = await dispatch(
      duplicateBudget({
        carryActual: fieldsValues.carryActual,
        fiscalPeriodStart: fieldsValues.year_start,
        id: fromBudget.id,
        title: fieldsValues.name,
      })
    );
    const createdBudget = get(data, 'payload', null);

    if (createdBudget) {
      onDuplicateBudget(createdBudget);
    }
  }, [dispatch, fieldsValues, fromBudget, onDuplicateBudget]);

  return (
    <Modal
      {...props}
      aria-label={intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.TITLE')}
      titleIcon={{
        name: 'copy-outline',
      }}
      data-testid='duplicate-budget_modal'
      title={intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.TITLE')}
      size='small'
      childrenClassName='pr-0'
      actionButtons={[
        {
          children: intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.TITLE'),
          onClick: handleDuplicateBudget,
          variant: 'primary',
          hasDisabledStyle: !canSubmit,
          'data-cy': 'duplicate-budget_button',
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: onCancel,
          'data-cy': 'cancel-button',
        },
      ]}
    >
      <div className='flex flex-col gap-y-4'>
        <FormItem>
          <TextField
            labelProps={{ required: true }}
            label={intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.FORM.NAME')}
            displayCharactersCount
            maxLength={LONG_INPUTS_LENGTH}
            defaultValue={fieldsValues.name}
            onChange={(e: any) =>
              updateValue({ [CREATE_BUDGET_FIELDS.NAME]: e.target.value })
            }
            placeholder={intl.get(
              'BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.PLACEHOLDERS.NAME'
            )}
            className='w-full'
          />
        </FormItem>

        <FormItem
          label={intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.FORM.START')}
        >
          <Dropdown
            onChange={handlePickYearStart}
            options={fiscalYearOptions}
            values={selectedFiscalYearOption ? [selectedFiscalYearOption] : []}
            triggerProps={{
              placeholder: intl.get(
                'BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.PLACEHOLDERS.START'
              ),
            }}
          />
        </FormItem>
        <div className='flex flex-row gap-x-2 items-center'>
          <Checkbox
            label={intl.get(
              'BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.CARRY_OVER_LABEL'
            )}
            onChange={(e) => {
              setFieldsValues((state) => ({
                ...state,
                carryActual: e.target.checked,
              }));
            }}
            inputProps={{
              className: 'text-neutral-dark',
            }}
            checked={fieldsValues.carryActual}
          />

          <Tooltip
            openMode='hover1'
            timeout={0}
            showPopper
            position='botomCenter'
            contentProps={{
              className: 'px-3 py-2 text-sm z-50 absolute',
            }}
            usePortal
            trigger={
              <IconButton
                description=''
                name='information-circle'
                iconClassname='pointer-events-none'
                className='text-lg text-neutral-dark hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
              />
            }
          >
            {intl.get('BUDGET_PAGE.DUPLICATE_BUDGET_MODAL.CARRY_OVER_POP_OVER')}
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
};
